import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Package, Plus } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import ListPageHeader from '../common/ListPageHeader.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import { mlSheetService } from '../../services/mlSheetService.js';
import useMlCreationPaidSettlement from '../../hooks/useMlCreationPaidSettlement.js';
import { getUnremittedPaidShipments, isShipmentDisabledForMl } from '../../utils/mlPaidSettlementRules.js';
import { useRequirePaidSettlementBeforeMl } from '../../hooks/useRequirePaidSettlementBeforeMl.js';
import MlPaidSettlementGate from './MlPaidSettlementGate.js';
import { canAddMlSheetShipments, hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import { useAuth } from '../../context/AuthContext.js';
import MlLocationChips from './MlLocationChips.js';
import MLSheetShipmentRecordList from './MLSheetShipmentRecordList.js';
import { toLocationsForMlDispatch } from '../../utils/hubDispatchFilter.js';
import {
  filterLocationsByQuery,
  formatLocationDropdownLabel,
} from '../../utils/locationDropdownLabel.js';
import {
  ML_MOBILE_DROPDOWN_ITEM,
  ML_MOBILE_DROPDOWN_ITEM_TEXT,
  ML_MOBILE_DROPDOWN_PANEL,
  ML_MOBILE_DROPDOWN_SEARCH,
  ML_MOBILE_FIELD_LABEL,
  ML_MOBILE_TRIGGER_TEXT,
  mlMobileTriggerClass,
} from '../../utils/mlSheetMobileDropdown.js';

const AddMlSheetShipments = () => {
  const { mlSheetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searching, setSearching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [locations, setLocations] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [fromIds, setFromIds] = useState(() => (
    user?.location_hub_id ? [user.location_hub_id] : []
  ));
  const [toIds, setToIds] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [locationSearch, setLocationSearch] = useState({ from: '', to: '' });
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const requestToken = useRef(0);
  const {
    loaded: mlBlockLoaded,
    mlCreationBlock,
    dues,
    agentDues,
    isHubAdmin,
    hardBlocked: mlHardBlocked,
    showGate: showPaidSettlementGate,
    settlementPath,
    settleOpen,
    setSettleOpen,
    refresh: refreshMlSettlement,
  } = useMlCreationPaidSettlement(user);

  const requiresPaidBeforeMl = useRequirePaidSettlementBeforeMl(user);
  const shipmentDisabledForMl = (shipment) => isShipmentDisabledForMl(shipment, user, requiresPaidBeforeMl);

  const load = useCallback(async (senderIds, receiverIds) => {
    const token = ++requestToken.current;
    if (!senderIds?.length && !receiverIds?.length) {
      setShipments([]);
      setSelected([]);
      setSearching(false);
      return;
    }
    try {
      setSearching(true);
      const params = {
        from_location_ids: senderIds.join(','),
        to_location_ids: receiverIds.join(','),
      };
      const response = await mlSheetService.getPickupAddableShipments(mlSheetId, params);
      if (token !== requestToken.current) return;
      setSheet(response.data?.ml_sheet || null);
      setShipments(response.data?.shipments || []);
      setSelected([]);
    } catch (err) {
      if (token !== requestToken.current) return;
      Alert.error(err.response?.data?.error || 'Failed to load shipments');
      navigate('/ml-sheets');
    } finally {
      if (token === requestToken.current) setSearching(false);
    }
  }, [mlSheetId, navigate]);

  useEffect(() => {
    if (!canAddMlSheetShipments(user)) {
      Alert.error('Add shipment is not available for this role');
      navigate('/ml-sheets');
      return undefined;
    }
    let cancelled = false;
    mlSheetService.getLocations()
      .then((locationsRes) => {
        if (cancelled) return;
        const locationsData = locationsRes.data?.hubs || locationsRes.data?.data || locationsRes.data || [];
        setLocations(Array.isArray(locationsData) ? locationsData : []);
      })
      .catch(() => {
        if (!cancelled) Alert.error('Failed to load branches');
      });
    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  useEffect(() => {
    if (!locations.length || fromIds.length) return;
    const own = locations.find((loc) => (
      String(loc.id || loc.hub_id) === String(user?.location_hub_id)
      || (user?.city && (
        loc.location === user.city
        || loc.hub_name === user.city
        || loc.name === user.city
      ))
    ));
    if (!own) return;
    setFromIds([own.id || own.hub_id]);
  }, [locations, fromIds.length, user?.location_hub_id, user?.city]);

  const fromKey = fromIds.join(',');
  const toKey = toIds.join(',');

  useEffect(() => {
    if (!canAddMlSheetShipments(user)) return undefined;
    load(fromIds, toIds);
    return undefined;
    // fromKey/toKey are the stable selection keys
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, load, fromKey, toKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !fromRef.current?.contains(event.target)
        && !toRef.current?.contains(event.target)
      ) {
        setOpenDropdown(null);
        setLocationSearch({ from: '', to: '' });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const receiverLocations = useMemo(
    () => toLocationsForMlDispatch(locations, fromIds, {
      enabled: hasInternalHubDispatchControls(user),
    }),
    [locations, fromIds, user],
  );

  const locationLabel = (loc) => (
    formatLocationDropdownLabel(loc, false)
    || loc.location
    || loc.hub_name
    || loc.name
    || ''
  );

  const isSelected = (ids, locationId) => ids.some((id) => String(id) === String(locationId));

  const toggleLocation = (side, locationId) => {
    const idStr = String(locationId);
    if (side === 'from') {
      const already = fromIds.some((id) => String(id) === idStr);
      const nextFrom = already
        ? fromIds.filter((id) => String(id) !== idStr)
        : [...fromIds, locationId];
      const eligible = new Set(
        toLocationsForMlDispatch(locations, nextFrom, {
          enabled: hasInternalHubDispatchControls(user),
        }).map((loc) => String(loc.id || loc.hub_id)),
      );
      setFromIds(nextFrom);
      setToIds((currentTo) => currentTo.filter((id) => eligible.has(String(id))));
      return;
    }
    setToIds((current) => (
      current.some((id) => String(id) === idStr)
        ? current.filter((id) => String(id) !== idStr)
        : [...current, locationId]
    ));
  };

  const visibleShipments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return shipments;
    return shipments.filter((shipment) => {
      const hay = [
        shipment.shipment_id,
        shipment.sender_location,
        shipment.receiver_location,
        shipment.sender_contact,
        shipment.receiver_contact,
      ].join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [shipments, search]);

  const hasBranchFilter = fromIds.length > 0 || toIds.length > 0;

  const sheetServiceNumber = useMemo(() => {
    const fromRoute = sheet?.route?.route_id || sheet?.route?.service_number;
    if (fromRoute) return String(fromRoute).trim();
    return '';
  }, [sheet]);

  const routeRestricted = Boolean(sheet?.route_id && sheetServiceNumber);

  const shipmentStatusClass = (status) => {
    const statusMap = {
      booked: 'bg-blue-100 text-blue-800',
      dispatched: 'bg-purple-100 text-purple-800',
      received: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return statusMap[String(status || '').toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const rowDisabled = (shipment) => !mlBlockLoaded || mlHardBlocked || shipmentDisabledForMl(shipment);

  const selectableIds = useMemo(
    () => visibleShipments.filter((shipment) => !rowDisabled(shipment)).map((shipment) => shipment.shipment_id),
    // rowDisabled follows the same inputs as the paid-settlement and block flags
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleShipments, mlBlockLoaded, mlHardBlocked, requiresPaidBeforeMl, user],
  );

  const selectAllChecked = selectableIds.length > 0 && selectableIds.every((id) => selected.includes(id));

  const handleSelectAll = () => {
    if (!mlBlockLoaded || mlHardBlocked) return;
    setSelected((prev) => {
      if (selectAllChecked) {
        const drop = new Set(selectableIds);
        return prev.filter((id) => !drop.has(id));
      }
      const have = new Set(prev);
      return [...prev, ...selectableIds.filter((id) => !have.has(id))];
    });
  };

  const toggle = (shipmentId) => {
    if (!mlBlockLoaded || mlHardBlocked) return;
    const shipment = shipments.find((s) => s.shipment_id === shipmentId);
    if (shipment && shipmentDisabledForMl(shipment)) {
      Alert.error('This Paid shipment has unremitted collections. Settle it first.');
      return;
    }
    setSelected((prev) => (
      prev.includes(shipmentId)
        ? prev.filter((id) => id !== shipmentId)
        : [...prev, shipmentId]
    ));
  };

  const handleAdd = async () => {
    if (!mlBlockLoaded) return;
    if (mlHardBlocked) {
      Alert.error(mlCreationBlock.reason || 'ML sheet creation is temporarily blocked');
      return;
    }
    const blockedSelected = requiresPaidBeforeMl
      ? getUnremittedPaidShipments(shipments, selected)
      : [];
    if (blockedSelected.length > 0) {
      Alert.error('Selected Paid shipment(s) have unremitted collections. Settle them first.');
      return;
    }
    if (!selected.length) {
      Alert.error('Select at least one shipment');
      return;
    }
    setSaving(true);
    try {
      await mlSheetService.addPickupShipments(mlSheetId, selected);
      Alert.success(`${selected.length} shipment(s) added to the ML sheet`);
      navigate(`/ml-sheets/view/${mlSheetId}`);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to add shipments');
    } finally {
      setSaving(false);
    }
  };

  const addDisabled = !mlBlockLoaded || mlHardBlocked || !selected.length;

  const renderLocationDropdown = (side, label, selectedIds, options, searchValue) => (
    <div className="relative min-w-0" ref={side === 'from' ? fromRef : toRef}>
      <label className={ML_MOBILE_FIELD_LABEL}>
        {label}
      </label>
      <div
        onClick={() => setOpenDropdown(openDropdown === side ? null : side)}
        className={mlMobileTriggerClass(openDropdown === side)}
      >
        <span className={ML_MOBILE_TRIGGER_TEXT}>
          {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select locations'}
        </span>
        <span className={`shrink-0 text-[10px] sm:text-sm transition ${openDropdown === side ? 'rotate-180 text-blue-500' : 'text-gray-500'}`}>▾</span>
      </div>
      {openDropdown === side && (
        <div className={ML_MOBILE_DROPDOWN_PANEL}>
          <input
            type="text"
            placeholder="Search locations..."
            value={searchValue}
            onChange={(e) => setLocationSearch((prev) => ({ ...prev, [side]: e.target.value }))}
            autoFocus
            className={ML_MOBILE_DROPDOWN_SEARCH}
          />
          <div className="max-h-48 overflow-y-auto">
            {options.length > 0 ? options.map((loc) => {
              const locId = loc.id || loc.hub_id;
              return (
                <label key={locId} className={ML_MOBILE_DROPDOWN_ITEM}>
                  <input
                    type="checkbox"
                    checked={isSelected(selectedIds, locId)}
                    onChange={() => toggleLocation(side, locId)}
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                  />
                  <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>{locationLabel(loc)}</span>
                </label>
              );
            }) : (
              <p className="text-[10px] sm:text-sm text-gray-500 p-2">No locations found</p>
            )}
          </div>
        </div>
      )}
      <div className="mt-1 min-w-0">
        <MlLocationChips
          compact
          locations={locations}
          selectedIds={selectedIds}
          onRemove={(id) => toggleLocation(side, id)}
        />
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto pb-6">
        <ListPageHeader title="Add Shipment" icon={Package} backTo="/ml-sheets" className="mb-4 sm:mb-6">
          <Button
            icon={Plus}
            loading={saving}
            disabled={addDisabled}
            onClick={handleAdd}
          >
            Add selected ({selected.length})
          </Button>
        </ListPageHeader>

        <MlPaidSettlementGate
          mlCreationBlock={mlCreationBlock}
          dues={dues}
          agentDues={agentDues}
          show={showPaidSettlementGate}
          hardBlocked={mlHardBlocked}
          settleOpen={settleOpen}
          setSettleOpen={setSettleOpen}
          onSettleSubmitted={refreshMlSettlement}
          settlementPath={settlementPath}
          allowSettle={!isHubAdmin}
          className="mb-4"
        />

        {sheet && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">ML Sheet</p>
              <p className="font-semibold break-all">{sheet.ml_sheet_id}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Service / route</p>
              <p className="font-semibold">{sheetServiceNumber || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Vehicle</p>
              <p className="font-semibold">{sheet.vehicle_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <p className="font-semibold">{sheet.status}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Shipments on sheet</p>
              <p className="font-semibold">{(sheet.shipment_ids || []).length}</p>
            </div>
          </div>
        )}

        {routeRestricted && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Only booked shipments on service <span className="font-semibold">{sheetServiceNumber}</span>{' '}
            can be added to this ML sheet. Shipments on other routes are hidden even if the sender
            and receiver branches match your filter.
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-3 sm:p-4 mb-4">
          <h3 className="font-semibold text-secondary text-sm sm:text-base mb-3">
            Sender & Receiver
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {renderLocationDropdown(
              'from',
              'Sender branch',
              fromIds,
              filterLocationsByQuery(locations, locationSearch.from),
              locationSearch.from,
            )}
            {renderLocationDropdown(
              'to',
              'Receiver branch',
              toIds,
              filterLocationsByQuery(receiverLocations, locationSearch.to),
              locationSearch.to,
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-3 sm:p-4 min-w-0 max-w-full">
          <div className="flex flex-wrap items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 min-w-0">
              <h3 className="font-semibold text-secondary text-sm sm:text-base whitespace-nowrap">
                Select Shipments ({selected.length}/{visibleShipments.length})
              </h3>
              {searching && <span className="text-xs text-blue-600">Searching...</span>}
            </div>
            {hasBranchFilter && visibleShipments.length > 0 && (
              <>
                <label className="flex xl:hidden items-center gap-2.5 cursor-pointer py-1 shrink-0">
                  <input
                    type="checkbox"
                    checked={selectAllChecked}
                    onChange={handleSelectAll}
                    className="w-5 h-5 flex-shrink-0 accent-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {selectAllChecked ? 'Deselect All' : 'Select All'}
                  </span>
                </label>
                <Button
                  variant="primary"
                  type="button"
                  onClick={handleSelectAll}
                  className="hidden xl:inline-flex w-auto shrink-0"
                >
                  {selectAllChecked ? 'Deselect All' : 'Select All'}
                </Button>
              </>
            )}
          </div>

          <div className="mb-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="GC number, location, or mobile"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
            />
          </div>

          <div className="min-w-0 max-w-full overflow-x-auto">
            {searching ? (
              <Loader fullScreen={false} size="small" message="Searching for shipments..." />
            ) : (
              <MLSheetShipmentRecordList
                shipments={hasBranchFilter ? visibleShipments : []}
                selectedShipmentIds={selected}
                onToggle={toggle}
                onSelectAll={handleSelectAll}
                selectAllChecked={selectAllChecked}
                isShipmentDisabled={rowDisabled}
                getStatusColor={shipmentStatusClass}
                emptyMessage={
                  hasBranchFilter
                    ? (
                      routeRestricted
                        ? `No booked shipments on ${sheetServiceNumber} for the selected branches.`
                        : 'No booked shipments available to add.'
                    )
                    : 'Select a sender or receiver branch to load shipments.'
                }
              />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddMlSheetShipments;
